import {
    ExpandMoreRounded as ExpandMoreRoundedIcon,
    VerifiedUserOutlined as VerifiedUserOutlinedIcon,
} from 'components/icons';
import { cn } from 'utils/cn';
import Loader from 'components/Loader.tsx';
import RelationshipPartnersAndManagers from 'components/misc/RelationshipPartnersAndManagers.tsx';
import PartyName from 'components/party/PartyName';
import type { CellContext } from '@tanstack/react-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { format } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import React, { useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useLazyGetCompliancesWithFiltersQuery } from 'store/api/contacts.ts';
import { ComplianceListItem, ComplianceStatisticsFilter } from 'types/compliance.ts';
import { PaginationProps } from 'types/paginationAndFilter.ts';
import { t } from 'utils/getTranslations.ts';
import { renderMatterIndicators } from 'utils/matterOptionRenderer';

import { TableRowCallForActionButton } from 'components/buttons/CallForActionButton.tsx';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes.tsx';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters.tsx';
import { useHasPermissions } from 'utils/permissionUtil';
import ComplianceExpandedContent from './ComplianceExpandedContent';
import { CheckStatus, VerificatoinStatusChip } from './ComplianceStatus.tsx';

import { getFilterOptionsFromClassifier } from 'components/tables/list-table/filterUtil.ts';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetMinGroupsListWithFiltersQuery } from 'store/api/groups';
import { useLazyGetMattersListQuery } from 'store/api/matters';
import { useLazyGetMinOfficesWithFiltersQuery } from 'store/api/offices';
import { useLazyGetPracticeAreasWithFiltersQuery } from 'store/api/practice-areas';
import { useLazyGetMinTeamsListWithFiltersQuery } from 'store/api/teams';
import { PracticeAreaType } from 'types/practice-areas';
import { TeamType } from 'types/teams.ts';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { useClassifiers } from 'utils/useClassifiers';

// `entity-link` hands the cell's link colour and wrapping to the table's own rule.
const LINK_CLASS = 'entity-link text-fg hover:text-link w-fit font-semibold no-underline hover:underline';

const USED_CLASSIFIERS = [
    'RISK_PROFILE_TYPE',
    'RISK_FACTOR_TYPE_CODE',
    'BUSINESS_AREA_TYPE',
    'COUNTRY',
    'MATTER_STATE_TYPE',
];

interface ComplianceTableProps {
    statisticsFilter: ComplianceStatisticsFilter | null;
}

const getComplianceHref = (item: ComplianceListItem) => `/contact/${item.partyId}/compliance`;

const ComplianceTable: React.FC<ComplianceTableProps> = ({ statisticsFilter }) => {
    const effectiveFormat = useEffectiveDateFormat();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });

    const [originalFetchData, { data, isLoading }] = useLazyGetCompliancesWithFiltersQuery();

    // Custom fetch function that adds AML filter when date range is used and statisticsFilter when active
    const fetchData = useCallback(
        (params: PaginationProps) => {
            const hasDateFilter = params.filters?.includes('matters.openedDate=');
            const amlFilter = 'matters.practiceAreas.practiceArea.isAmlApplicable==true';

            // The office filter keeps the contacts selector, so remembered filters and shared links still
            // restore it; this list sends it as its own selector, which counts every live case.
            // Likewise the "Client since" range is stored under `createdAt`, the key saved views and shared
            // links hold, and is sent as the selector that reads the client-since date.
            let updatedParams = {
                ...params,
                filters: params.filters
                    ?.replaceAll('matters.handledByOffice.id', 'allMatters.handledByOffice.id')
                    .replaceAll('createdAt=', 'effectiveClientSince='),
            };

            if (hasDateFilter && updatedParams.filters) {
                if (!updatedParams.filters.includes(amlFilter)) {
                    updatedParams = { ...updatedParams, filters: `${updatedParams.filters};${amlFilter}` };
                }
            }

            if (statisticsFilter) {
                updatedParams = {
                    ...updatedParams,
                    extraParams: { ...updatedParams.extraParams, statisticsFilter },
                };
            }

            return originalFetchData(updatedParams);
        },
        [originalFetchData, statisticsFilter]
    );

    const hasMatterReadPermissions = useHasPermissions([
        'READ.MATTERS.MY_CASES',
        'READ.MATTERS.MY_TEAM',
        'READ.MATTERS.MY_TEAM.NON_CONFIDENTIAL',
        'READ.MATTERS.CONFIDENTIAL',
        'READ.MATTERS.NON_CONFIDENTIAL',
        'READ.MATTERS.NON_CONFIDENTIAL_MY_CONTACTS',
    ]);

    const columns = useMemo<DataTableColumnDef<ComplianceListItem>[]>(
        () => [
            {
                header: t('compliance.name-client-since-verification-status'),
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 260,
                },
                cell: ({ row }) => {
                    const {
                        partyId,
                        name,
                        complianceVerificationStatusTypeCode: status,
                        clientSince,
                        createdAt,
                    } = row.original;
                    const dateValue = clientSince ?? createdAt;
                    const formattedDate = dateValue ? format(new Date(dateValue), effectiveFormat) : '';
                    return (
                        <div className="flex flex-col gap-2">
                            <RouterLink className={LINK_CLASS} to={`/contact/${partyId}/compliance`}>
                                <PartyName party={{ ...row.original, name }} variant="list" showPartyNumber />
                            </RouterLink>
                            {formattedDate && t('compliance.client-since-date', { date: formattedDate })}
                            <VerificatoinStatusChip status={status} />
                        </div>
                    );
                },
            },
            {
                header: t('status.status'),
                enableSorting: false,
                meta: {
                    widthWeight: 1.06,
                    minPx: 240,
                },
                cell: ({ row }) => {
                    const { complianceChecks } = row.original;
                    return (
                        <div className="flex flex-col gap-1">
                            {complianceChecks?.map((check, index) => (
                                // A party can hold several checks of one type (every manual check is MANUAL).
                                <CheckStatus key={`${check.complianceCheckTypeCode}-${index}`} check={check} />
                            ))}
                        </div>
                    );
                },
            },
            {
                header: t('contacts.relationship-owners'),
                enableSorting: false,
                meta: {
                    widthWeight: 1.04,
                    minPx: 240,
                },
                cell: ({ row }) => {
                    const { partners, managers } = row.original;
                    return (
                        <RelationshipPartnersAndManagers
                            relationshipPartners={partners}
                            relationshipManagers={managers}
                        />
                    );
                },
            },
            ...(hasMatterReadPermissions
                ? [
                      {
                          id: 'expander',
                          enableSorting: false,
                          meta: {
                              // Expander column: sized to its content, not a share of the flex pool.
                              fixedPx: 64,
                          },
                          cell: ({ row }: CellContext<ComplianceListItem, unknown>) => (
                              <TableRowCallForActionButton
                                  onClick={row.getToggleExpandedHandler()}
                                  style={{ cursor: 'pointer' }}
                                  aria-label={t(row.getIsExpanded() ? 'collapse-row' : 'expand-row')}
                                  shape="rounded"
                              >
                                  <ExpandMoreRoundedIcon
                                      className={cn(
                                          'new:motion-safe:transition-transform new:duration-(--duration-fast) new:ease-(--ease-smooth-out)',
                                          row.getIsExpanded() && '-scale-y-100'
                                      )}
                                  />
                              </TableRowCallForActionButton>
                          ),
                      },
                  ]
                : []),
        ],
        [hasMatterReadPermissions, effectiveFormat]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [
                {
                    selector: 'riskProfileTypeCode',
                    comperator: '==',
                    filters: [
                        {
                            label: t('compliance.only-high-risk'),
                            argument: 'HIGH',
                        },
                    ],
                },
                {
                    selector: 'id',
                    comperator: '=isUboIssue=',
                    filters: [
                        {
                            label: t('compliance.ubo-issues'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    selector: 'id',
                    comperator: '=isAmlIssue=',
                    filters: [
                        {
                            label: t('compliance.aml-issues'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    selector: 'id',
                    comperator: '=isNotVerified=',
                    filters: [
                        {
                            label: t('compliance.filter.only-not-verified'),
                            argument: 'true',
                        },
                    ],
                },
            ],
            filters: [
                {
                    type: 'text',
                    label: t('contacts.filter.contact-name'),
                    group: 'compliance.client',
                    selector: 'name',
                    multiple: true,
                },
                {
                    type: 'dynamicmultiselect',
                    group: 'compliance.client',
                    label: t('contacts.filter.member-of-the-group'),
                    tooltip: 'contacts.tooltips.member-of-groups',
                    selector: 'id',
                    comperator: '=ismemberofgroup=',
                    paginated: {
                        fetch: useLazyGetMinGroupsListWithFiltersQuery,
                        searchField: 'groupName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'groupName',
                    },
                },
                {
                    type: 'multiselect',
                    label: t('contacts.business-area'),
                    selector: 'businessAreas.businessAreaCode',
                    group: 'compliance.client',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['BUSINESS_AREA_TYPE']) : [],
                },
                {
                    type: 'dynamicmultiselect',
                    group: 'compliance.client',
                    label: t('contacts.filter.sector-group'),
                    tooltip: 'contacts.tooltips.sector-group',
                    selector: 'id',
                    comperator: '=hasbusinessareasinsectorgroup=',
                    paginated: {
                        fetch: useLazyGetMinTeamsListWithFiltersQuery,
                        searchField: 'name',
                        filters: `teamTypeCode==${TeamType.SECTOR_GROUP};isParent==true`,
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    group: 'compliance.client',
                    label: t('contacts.about.relationship-partner'),
                    selector: 'partners.employee.id',
                    paginated: {
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    group: 'compliance.client',
                    label: t('compliance.filter.responsible-partner-from-office'),
                    tooltip: 'compliance.tooltips.responsible-partner-from-office',
                    selector: 'id',
                    comperator: '=hasrelationshippartnerfromoffice=',
                    paginated: {
                        fetch: useLazyGetMinOfficesWithFiltersQuery,
                        searchField: 'name',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                    getOptionEndAdornment: officeInactiveSuffix,
                },
                {
                    type: 'multiselect',
                    label: t('address-form.country'),
                    selector: 'addresses.countryCode',
                    group: 'compliance.client',
                    tooltip: 'contacts.tooltips.country',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['COUNTRY']) : [],
                },
                {
                    type: 'multiselect',
                    label: t('compliance.filter.risk-profile'),
                    selector: 'riskProfileTypeCode',
                    group: 'compliance.client',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['RISK_PROFILE_TYPE']) : [],
                },
                {
                    type: 'multiselect',
                    label: t('compliance.filter.risk-factors'),
                    selector: 'riskFactors.riskFactorTypeCode',
                    group: 'compliance.client',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['RISK_FACTOR_TYPE_CODE']) : [],
                },
                {
                    type: 'offset-date-time-range',
                    label: t('contacts.filter.client-since-from'),
                    labelTo: t('contacts.filter.client-since-to'),
                    tooltip: 'contacts.tooltips.client-since-filter',
                    shouldDisplayQuickPick: false,
                    group: 'compliance.client',
                    selector: 'createdAt',
                },
                {
                    type: 'checkboxgroup',
                    selector: 'id',
                    comperator: '=isNotVerified=',
                    group: 'compliance.client',
                    filters: [
                        {
                            tooltip: 'compliance.tooltips.only-not-verified',
                            label: t('compliance.filter.only-not-verified'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    type: 'checkboxgroup',
                    selector: 'id',
                    comperator: '=hasnosanctionscheck=',
                    group: 'compliance.client',
                    filters: [
                        {
                            tooltip: 'compliance.tooltips.only-without-sanctions-check',
                            label: t('compliance.filter.only-without-sanctions-check'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    type: 'checkboxgroup',
                    selector: 'id',
                    comperator: '=hasnoserviceagreement=',
                    group: 'compliance.client',
                    filters: [
                        {
                            tooltip: 'compliance.tooltips.only-without-service-agreements',
                            label: t('compliance.filter.only-without-service-agreements'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    type: 'checkboxgroup',
                    selector: 'id',
                    comperator: '=hasnouboinfo=',
                    group: 'compliance.client',
                    filters: [
                        {
                            tooltip: 'compliance.tooltips.only-without-ubo-info',
                            label: t('compliance.filter.only-without-ubo-info'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    type: 'checkboxgroup',
                    selector: 'id',
                    comperator: '=hasamlcases=',
                    group: 'compliance.client',
                    filters: [
                        {
                            tooltip: 'compliance.tooltips.only-with-aml-cases',
                            label: t('compliance.filter.only-with-aml-cases'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    type: 'checkboxgroup',
                    selector: 'id',
                    comperator: '=hasnoamlform=',
                    group: 'compliance.client',
                    filters: [
                        {
                            tooltip: 'compliance.tooltips.only-without-aml-forms',
                            label: t('compliance.filter.only-without-aml-forms'),
                            argument: 'true',
                        },
                    ],
                },
                {
                    type: 'dynamicmultiselect',
                    selector: 'matters.id',
                    label: t('matters.filter.case-name'),
                    group: 'compliance.cases',
                    getOptionStartAdornment: renderMatterIndicators,
                    paginated: {
                        fetch: useLazyGetMattersListQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'fullName',
                    },
                },
                {
                    type: 'multiselect',
                    label: t('matters.filter.case-status'),
                    selector: 'matters.matterStateTypeCode',
                    group: 'compliance.cases',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['MATTER_STATE_TYPE']) : [],
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('matters.filter.handled-by-office'),
                    selector: 'matters.handledByOffice.id',
                    group: 'compliance.cases',
                    paginated: {
                        fetch: useLazyGetMinOfficesWithFiltersQuery,
                        searchField: 'name',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                    getOptionEndAdornment: officeInactiveSuffix,
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('matters.filter.practice-areas'),
                    selector: 'matters.practiceAreas.practiceArea.id',
                    group: 'compliance.cases',
                    tooltip: 'matters.tooltips.filter-cases-practice-area',
                    getOptionStartAdornment: (option) => option.taxonomyId && `[${option.taxonomyId}]`,
                    paginated: {
                        fetch: useLazyGetPracticeAreasWithFiltersQuery,
                        searchField: 'name',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                        filters: `practiceAreaTypeCode==${PracticeAreaType.PRACTICE_AREA}`,
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('matters.filter.supervising-partner'),
                    selector: 'matters.supervisedByEmployee.id',
                    group: 'compliance.cases',
                    paginated: {
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('matters.responsible-lawyer'),
                    selector: 'matters.responsibleEmployee.id',
                    group: 'compliance.cases',
                    paginated: {
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'daterange',
                    label: t('contacts.filter.case-opening-date-from'),
                    labelTo: t('contacts.filter.case-opening-date-to'),
                    shouldDisplayQuickPick: false,
                    group: 'compliance.cases',
                    selector: 'matters.openedDate',
                },
            ],
            searchBarSelectors: [
                { selector: 'name' },
                { selector: 'partners.employee.fullName' },
                { selector: 'managers.employee.fullName' },
            ],
        }),
        [classifiers]
    );

    if (isLoading || isLoadingClassifiers) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<ComplianceListItem>
            key={statisticsFilter ?? 'all'}
            getRowHref={getComplianceHref}
            // Managed v8 engine for the weighted-width layout; column picker / reorder / resize
            // stay OFF (flags default false) — this table never had them.
            columnConfigId="compliance"
            // The page is not a bounded scroller (PaddedLayout grows to content), so cap the
            // table's height here to engage windowing.
            dataTableClassName="max-h-[calc(100vh-24rem)]"
            columns={columns}
            data={data}
            isLoading={isLoading}
            pluralTextId="contacts.contacts"
            singularTextId="contacts.contact"
            noEntriesId="contacts.no-contacts-found"
            noEntriesBodyId="compliance.empty.body"
            noEntriesIcon={VerifiedUserOutlinedIcon}
            fetchData={fetchData}
            filterConfig={filterConfig}
            rowsPerPage={50}
            renderExpandedContent={(row) => <ComplianceExpandedContent {...row} />}
        />
    );
};

export default ComplianceTable;
